<template>
  <AuthLayout :mode="mode">
    <Section
      :heading="member?.company_name ? member?.company_name : ''"
      :mode="mode"
    />
    <div class="grid gap-6">
      <div v-if="member?.logo_url" class="flex justify-center sm:justify-start">
        <img
          :src="member.logo_url"
          :alt="`${member.company_name || ''} logo`"
          class="max-h-[120px] max-w-[400px] object-contain"
          loading="lazy"
        />
      </div>
      <AppCard
        :mode="mode"
        title="Member Details"
        :isSmallTitle="true"
        :isCenter="false"
      >
        <template #body>
          <RecordDetails :mode="mode" :data="memberDetails" />
        </template>
      </AppCard>
      <AppCard
        v-if="isAdmin"
        :mode="mode"
        title="Back Office"
        :isSmallTitle="true"
        :isCenter="false"
      >
        <template #body>
          <RecordDetails :mode="mode" :data="backOfficeDetails" />
        </template>
      </AppCard>
      <AppCard
        :mode="mode"
        title="Manufacturers we Specialize in"
        :isSmallTitle="true"
        :isCenter="false"
      >
        <template #body>
          <template v-if="manufactuers && manufactuers.length > 0">
            <div class="grid grid-cols-3 gap-2 sm:grid-cols-1">
              <div
                v-for="(mfr, index) in manufactuers"
                :key="index"
                class="rounded-lg border p-5 text-center"
                :class="[
                  mode === 'dark' ? 'border-dark-border' : 'border-border-10',
                ]"
              >
                <span class="text-[15px] font-medium sm:text-[14px]">{{
                  mfr.company
                }}</span>
              </div>
            </div>
          </template>
          <EmptyData
            v-else
            :notFound="'No manufacturers represented!'"
            :mode="mode"
          />
        </template>
      </AppCard>
    </div>
  </AuthLayout>
</template>

<script>
import Section from "@C/Common/Section.vue";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import { useGlobalErrorHandler } from "@L/errorHandler";
import ApiRequest from "@L/ApiRequest";
import EmptyData from "@C/Common/EmptyData.vue";
import AppCard from "@C/Common/AppCard.vue";
import RecordDetails from "@C/Common/RecordDetails.vue";
import AuthLayout from "@A/AuthLayout.vue";

export default {
  name: "MemberDetails",
  components: {
    AuthLayout,
    Section,
    EmptyData,
    AppCard,
    RecordDetails,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      handleError,
    };
  },
  props: {
    mode: String,
  },
  data() {
    return {
      id: "",
      memberData: null,
    };
  },
  created() {
    this.id = this.$route.params.id;
  },
  mounted() {
    this.getMember();
  },
  methods: {
    async getMember() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/member/${this.id}`);
        if (data && data.member) {
          this.memberData = data;
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
  },
  computed: {
    member() {
      return this.memberData?.member;
    },
    backOfficeMember() {
      return this.memberData?.back_office_member;
    },
    manufactuers() {
      return this.memberData?.member?.mfrs.filter((mfr) => mfr.selected);
    },
    isAdmin() {
      return this.userStore.user.global_admin;
    },
    memberDetails() {
      return [
        {
          name: "Address",
          value: [
            this.member?.address1,
            this.member?.address2,
            this.member?.city,
            this.member?.state_region,
            this.member?.zip,
          ]
            .filter(Boolean)
            .join(", "),
        },
        {
          name: "Phone",
          value: this.member?.company_phone1 || this.member?.company_phone2,
        },
        {
          name: "Fax",
          value: this.member?.fax,
        },
        {
          name: "Email",
          value: this.member?.company_email,
        },
        {
          name: "URL",
          value: this.member?.url,
        },
        {
          name: "Type",
          value: "",
          isCheckboxes: {
            enabled: true,
            values: {
              member: this.member?.assoc_member,
              subscriber: this.member?.network_subscriber,
            },
          },
        },
      ];
    },
    backOfficeDetails() {
      return [
        {
          name: "Annual Dues",
          value: this.backOfficeMember?.annual_dues,
        },
        {
          name: "Banner Ad",
          value: this.backOfficeMember?.banner_ad,
        },
        {
          name: "Banner Image",
          value: this.backOfficeMember?.banner_image,
        },
        {
          name: "Banner URL",
          value: this.backOfficeMember?.banner_url,
        },
        {
          name: "CC Exp Date",
          value: this.backOfficeMember?.cc_exp_date,
        },
        {
          name: "CC Last Four",
          value: this.backOfficeMember?.cc_last_four,
        },
        {
          name: "CC Name",
          value: this.backOfficeMember?.cc_name,
        },
        {
          name: "CC Type",
          value: this.backOfficeMember?.cc_type,
        },
        {
          name: "Company Info",
          value: this.backOfficeMember?.company_info,
        },
        {
          name: "Conference Member Rate",
          value: this.backOfficeMember?.conference_member_rate,
        },
        {
          name: "Data Source",
          value: this.backOfficeMember?.data_source,
        },
        {
          name: "Ethics Non Participant",
          value: this.backOfficeMember?.ethics_non_participant,
        },
        {
          name: "Ethics Violator",
          value: this.backOfficeMember?.ethics_violator,
        },
        {
          name: "Member Since",
          value: this.backOfficeMember?.member_since,
          isDate: true,
        },
        {
          name: "Network Paid Thru",
          value: this.backOfficeMember?.networ_paid_thru,
        },
        {
          name: "Network Amount",
          value: this.backOfficeMember?.network_amount,
        },
        {
          name: "Network Start Date",
          value: this.backOfficeMember?.network_start_date,
          isDate: true,
        },
        {
          name: "Notes",
          value: this.backOfficeMember?.notes,
        },
        {
          name: "Vat Number",
          value: this.backOfficeMember?.vat_number,
        },
      ];
    },
  },
};
</script>
